import { Typography } from "@mui/material";
import { get } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import Title from "src/components/TableV2/Title";
import { IIODncEmail } from "src/store/models/campaignDncEmail.models";
import { UserRole } from "src/store/models/user.model";
import { fetchDncEmailList, removeDncEmail } from "src/store/reducers/campaignDncEmail";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import { openUrl } from "src/util/util.navigation";
import DisTable from "../../components/TableV2/Table";
import * as S from "../../constants/StringConstants";
import { IRootState } from "../../store/reducers";
import "./CampaignDncEmail.scss";

interface ICampaignDncEmailProps extends StateProps, DispatchProps, RouteComponentProps {}

interface ICampaignDncEmailState {
  deletedId: number | null;
  deletedEmail: string | null;
}

class CampaignDncEmail extends React.Component<ICampaignDncEmailProps, ICampaignDncEmailState> {
  constructor(props: ICampaignDncEmailProps) {
    super(props);
    this.state = { deletedId: null, deletedEmail: null };
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    this.handleDeleteCancel = this.handleDeleteCancel.bind(this);
  }

  handleDeletePopup = (id: number, email: string) => {
    this.setState({ deletedId: id, deletedEmail: email });
  };

  async handleDeleteConfirm() {
    const { deletedId } = this.state;
    if (deletedId !== null) {
      await this.props.removeDncEmail(deletedId);
      await this.props.fetchDncEmailList();
      this.setState({ deletedId: null, deletedEmail: null });
    }
  }

  handleDeleteCancel() {
    this.setState({ deletedId: null, deletedEmail: null });
  }

  render() {
    const {
      auth,
      fetchDncEmailList,
      dncEmail: { map, projection, total, listOptions, loading },
    } = this.props;

    const isAdmin = hasAnyAuthority(auth.user, [UserRole.Admin]);

    const columns = [
      {
        key: "email",
        title: S.DNC_COL_EMAIL,
        sortKey: "email",
      },
      ...(isAdmin
        ? [
            {
              key: "delete",
              title: S.AFF_DELETE ?? "Delete",
              style: { textAlign: "right" },
              render: (item: IIODncEmail) => (
                <Icon
                  name={IconName.DeleteFileBlue}
                  size="16px"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleDeletePopup(item.id, item.email);
                  }}
                />
              ),
            },
          ]
        : []),
    ];

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container">
          <div className="table-view__header-container">
            <AppBar title={S.DNC_EMAIL_TITLE} icon={IconName.Campaigns} loading={loading} />
          </div>

          <div className="table-view__buttons-container">
            <GuardedComponent authorizedRoles={[UserRole.Admin]}>
              <button className="side-nav__add-button" onClick={() => openUrl("/dnc/email/new", true)}>
                <Icon name={IconName.Plus} />
                {S.DNC_NEW_EMAIL}
              </button>

              <button className="side-nav__add-button" onClick={() => openUrl("/dnc/phone/new", true)}>
                <Icon name={IconName.Plus} />
                {S.DNC_NEW_PHONE}
              </button>
            </GuardedComponent>
          </div>
        </div>

        <DisTable<IIODncEmail>
          columns={columns}
          initialState={{ rows: 25, projection, total, map, ...listOptions }}
          onUpdate={async ({ page, rows, sort, query }) => {
            const result: any = await fetchDncEmailList({ page, rows, sort, query });
            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as IIODncEmail[],
            };
          }}>
          <FilterContainer left={<Title text={S.DNC_EMAIL_TITLE} />}></FilterContainer>
        </DisTable>

        {this.state.deletedId !== null && (
          <PromptModal
            open
            title={S.DNC_EMAIL_TITLE}
            confirmText={S.DNC_EMAIL_DELETE}
            rejectText={S.DNC_EMAIL_CANCEL_DELETE}
            onConfirm={this.handleDeleteConfirm}
            onReject={this.handleDeleteCancel}>
            <Typography variant="h6">{S.DNC_EMAIL_DELETE_CONFIRMATION?.(this.state.deletedEmail)}</Typography>
          </PromptModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ dncEmail, auth }: IRootState) => ({ dncEmail, auth });
const mapDispatchToProps = { fetchDncEmailList, removeDncEmail };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(CampaignDncEmail);
